import { IArticle } from '@/src/interfaces/generated/contentful';
import { ArticleCard } from '@/src/packages/components/ui/ArticleCard';
import { Container } from '@/src/packages/components/ui/Container';
import { PageSection } from '@/src/packages/components/ui/PageSection';
import { PageSectionTitle } from '@/src/packages/components/ui/PageSection/PageSectionTitle';
import { paths } from '@/src/packages/routes';
import { pathParams } from '@/src/packages/routes/pathParams.enum';
import { getRoute } from '@/src/packages/utils/routes';
import { SwiperSlide } from 'swiper/react';
import { Swiper } from 'swiper/react';
import { useRef, useState } from 'react';
import { Box, IBoxProps } from '@/src/packages/components/ui/Box';
import type SwiperCore from 'swiper';
import { cn } from '@/src/packages/utils/cn';
import { screens } from '@/src/packages/utils/generateImageSizes';
import { Center } from '@/src/packages/components/ui/Center';
import { ArrowLeft, ArrowRight } from '@carbon/icons-react';
import { Mousewheel, Keyboard } from 'swiper/modules';

type IArrowProps = IBoxProps & { direction: 'next' | 'prev' };

export const Arrow = (props: IArrowProps) => {
  const { direction, ...rest } = props;

  return (
    <Box
      as="button"
      className={cn(
        'h-10 w-10 cursor-pointer rounded-full border transition-all enabled:border-black hover:enabled:bg-black hover:enabled:text-white disabled:cursor-default disabled:opacity-50',
      )}
      aria-label={direction === 'prev' ? 'Go back' : 'Go forwards'}
      {...rest}
    >
      <Center>
        {direction === 'prev' ? (
          <ArrowLeft size={20} />
        ) : (
          <ArrowRight size={20} />
        )}
      </Center>
    </Box>
  );
};

export const Arrows = ({
  isBeginning,
  isEnd,
  handleNext,
  handlePrev,
}: {
  isBeginning: boolean;
  isEnd: boolean;
  handleNext(): void;
  handlePrev(): void;
}) => {
  return (
    <Box className="flex space-x-2">
      <Arrow direction="prev" disabled={isBeginning} onClick={handlePrev} />
      <Arrow direction="next" disabled={isEnd} onClick={handleNext} />
    </Box>
  );
};

const JournalEntryRelated = ({ articles }: { articles: IArticle[] }) => {
  const swiperRef = useRef<SwiperCore>();

  const [isEnd, setEnd] = useState(false);

  const [isBeginning, setBeginning] = useState(true);

  const [active, setActive] = useState(0);

  return (
    <PageSection className="py-12">
      <Container className="space-y-8">
        <Box className="flex items-center">
          <PageSectionTitle marginBottom={0} className="flex-1">
            More articles
          </PageSectionTitle>

          <Arrows
            isEnd={isEnd}
            isBeginning={isBeginning}
            handlePrev={() => swiperRef.current?.slideTo(active - 1)}
            handleNext={() => swiperRef.current?.slideTo(active + 1)}
          />
        </Box>

        <Box className="-mx-gutter lg:mx-0">
          <Swiper
            modules={[Mousewheel, Keyboard]}
            onInit={(swiper) => {
              swiperRef.current = swiper;
            }}
            onSlideChange={(swiper) => {
              setEnd(swiper.isEnd);
              setBeginning(swiper.isBeginning);
              setActive(swiper.activeIndex);
            }}
            onResize={(swiper) => {
              setActive(swiper.activeIndex);
            }}
            spaceBetween={48}
            slidesPerView={1.1}
            keyboard={{
              enabled: true,
            }}
            className="!px-gutter lg:!px-0"
            breakpoints={{
              [screens.md]: {
                slidesPerView: 2,
              },
              [screens.lg]: {
                slidesPerView: 3,
              },
              [screens.xl]: {
                slidesPerView: 4,
              },
            }}
          >
            {articles.map((article, i) => {
              return (
                <SwiperSlide key={i}>
                  <ArticleCard
                    title={article.fields.title || ''}
                    categories={article.fields.categories}
                    summary={article.fields.meta.fields.description}
                    publishedAt={article.fields.publicationDate}
                    coverImageUrl={article.fields.cover.fields.file.url}
                    href={getRoute({
                      route: paths.BLOG_SINGLE,
                      params: {
                        [pathParams.BLOG_SINGLE]: article.fields.slug,
                      },
                    })}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </Box>
      </Container>
    </PageSection>
  );
};

export { JournalEntryRelated };
